// path: frontend/src/components/RequireAuth.tsx
import React from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";

type RequireAuthProps = {
  children: React.ReactElement;
  fallback?: React.ReactNode;
  redirectTo?: string;
};

/**
 * Protect authenticated routes without redirecting before Firebase finishes
 * restoring its persisted browser session.
 */
export default function RequireAuth({
  children,
  fallback,
  redirectTo = "/login",
}: RequireAuthProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <>
        {fallback ?? (
          <div className="flex items-center justify-center py-16 text-sm text-slate-500 dark:text-slate-400">
            Checking session…
          </div>
        )}
      </>
    );
  }

  if (!user) {
    const next = `${location.pathname}${location.search}${location.hash}`;

    return (
      <Navigate
        to={`${redirectTo}?next=${encodeURIComponent(next)}`}
        state={{ from: location }}
        replace
      />
    );
  }

  return children;
}
